import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getContentFontStyle, getHeadingFontStyle } from '../../lib/contentBlockFontSize';
import { getThemeColorCssValue, normalizeColorValue } from '../../lib/themeColors';
import { getIconFallbackLabel } from '../../lib/uiText';
import { resolveUrlForLocale } from '../../lib/localizedSlugs';
import styles from './Description.module.css';

const VALID_TEXT_ALIGN = new Set(['left', 'center', 'right']);
const DESCRIPTION_FONT_SIZE_FALLBACKS = {
  heading: '1rem',
  paragraph: '1rem',
  list: '1rem',
  button: '0.95em',
};

const Description = ({ data, anchorId }) => {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const { locale } = useGlobal();
  const contentBlocks = Array.isArray(data?.content) ? data.content.filter(Boolean) : [];

  const iconMedia = data?.icon?.url
    ? getStrapiMediaAttributes(data.icon, {
      altFallback: getIconFallbackLabel(locale, 'section'),
      titleFallback: getIconFallbackLabel(locale, 'section'),
    })
    : null;
  const themeColor = normalizeColorValue(data?.themeColor);
  const backgroundColor = normalizeColorValue(data?.backgroundColor);
  const resolvedBackgroundColor = getThemeColorCssValue(themeColor) || backgroundColor;
  const textAlign = VALID_TEXT_ALIGN.has(data?.textAlign) ? data.textAlign : 'left';
  const useWhiteText = Boolean(resolvedBackgroundColor && data?.useWhiteText);
  const useBoldText = Boolean(data?.useBoldText);
  const isTitleOnly = contentBlocks.length > 0
    && !iconMedia
    && contentBlocks.every((block) => block?.__component === 'shared.heading');

  const sectionClassName = [
    styles.description,
    resolvedBackgroundColor ? styles['description--with-background'] : styles['description--transparent'],
    styles[`description--align-${textAlign}`],
    useWhiteText ? styles['description--white'] : null,
    useBoldText ? styles['description--bold'] : null,
    isTitleOnly ? styles['description--title-only'] : null,
  ].filter(Boolean).join(' ');

  const sectionStyle = {
    ...(resolvedBackgroundColor ? { backgroundColor: resolvedBackgroundColor } : {}),
  };

  const renderContentBlock = (block, index) => {
    if (!block) return null;

    switch (block.__component) {
      case 'shared.heading': {
        const level = block.level || 'h2';
        const Tag = level;
        const headingStyle = getHeadingFontStyle(block?.fontSize, level, {
          default: DESCRIPTION_FONT_SIZE_FALLBACKS.heading,
        });
        const headingClassName = [
          styles.description__heading,
          styles[`description__heading--${level}`],
          styles['description__animate-item'],
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <Tag key={`${level}-${index}`} className={headingClassName} style={headingStyle}>
            {block.text}
          </Tag>
        );
      }
      case 'shared.paragraph': {
        const paragraphStyle = getContentFontStyle(
          block?.fontSize,
          DESCRIPTION_FONT_SIZE_FALLBACKS.paragraph
        );
        return (
          <p
            key={`paragraph-${index}`}
            className={`${styles.description__text} ${styles['description__animate-item']}`}
            style={paragraphStyle}
          >
            {block.text}
          </p>
        );
      }
      case 'shared.list': {
        const listItemStyle = getContentFontStyle(block?.fontSize, DESCRIPTION_FONT_SIZE_FALLBACKS.list);
        const hasIcon = block.icon?.url;
        const listIconMedia = hasIcon
          ? getStrapiMediaAttributes(block.icon, {
            altFallback: getIconFallbackLabel(locale, 'list'),
            titleFallback: getIconFallbackLabel(locale, 'list'),
          })
          : null;
        const ListTag = !hasIcon && block.ordered ? 'ol' : 'ul';
        const listClassName = [
          styles.description__list,
          hasIcon ? styles['description__list--icons'] : null,
          !hasIcon && block.ordered ? styles['description__list--ordered'] : null,
        ].filter(Boolean).join(' ');

        return (
          <ListTag key={`list-${index}`} className={listClassName}>
            {block.items?.map((item, itemIndex) => (
              <li
                key={`list-item-${itemIndex}`}
                className={`${styles['description__list-item']} ${styles['description__animate-item']}`}
                style={listItemStyle}
              >
                {listIconMedia?.src && (
                  <img
                    className={styles['description__list-icon']}
                    src={listIconMedia.src}
                    alt={listIconMedia.alt}
                    title={listIconMedia.title}
                    aria-hidden="true"
                  />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ListTag>
        );
      }
      case 'shared.image': {
        const imageMedia = getStrapiMediaAttributes(block.image, {
          altFallback: block.alt || '',
          titleFallback: block.alt || '',
          preferredFormats: ['large', 'medium', 'small'],
        });
        if (!imageMedia.src) return null;
        return (
          <img
            key={`image-${index}`}
            className={`${styles['description__content-image']} ${styles['description__animate-item']}`}
            src={imageMedia.src}
            alt={imageMedia.alt}
            title={imageMedia.title}
            width={imageMedia.width}
            height={imageMedia.height}
            loading="lazy"
            decoding="async"
          />
        );
      }
      case 'shared.section-button': {
        if (!block.label || !block.url) return null;
        const alignment = block.alignment || 'left';
        const isExternal = /^https?:\/\//i.test(block.url) || block.openInNewTab;
        const resolvedUrl = resolveUrlForLocale(block.url, locale);
        const buttonStyle = getContentFontStyle(block?.fontSize, DESCRIPTION_FONT_SIZE_FALLBACKS.button);
        const btnIconMedia = block?.icon?.url
          ? getStrapiMediaAttributes(block.icon, {
            altFallback: getIconFallbackLabel(locale, 'button', block.label),
            titleFallback: getIconFallbackLabel(locale, 'button', block.label),
          })
          : null;
        const btnClass = [
          styles['description__btn'],
          block.darkText ? styles['description__btn--dark'] : '',
        ].filter(Boolean).join(' ');
        const wrapClass = [
          styles['description__btnWrap'],
          styles[`description__btnWrap--${alignment}`],
        ].join(' ');
        const content = (
          <>
            {btnIconMedia?.src && (
              <img src={btnIconMedia.src} alt={btnIconMedia.alt} title={btnIconMedia.title} className={styles['description__btnIcon']} aria-hidden="true" />
            )}
            <span>{block.label}</span>
          </>
        );
        return (
          <div key={`btn-${index}`} className={wrapClass}>
            {isExternal ? (
              <a
                href={resolvedUrl}
                className={btnClass}
                style={buttonStyle}
                target={block.openInNewTab ? '_blank' : undefined}
                rel={block.openInNewTab ? 'noopener noreferrer' : undefined}
                title={block.label}
              >
                {content}
              </a>
            ) : (
              <Link to={resolvedUrl} className={btnClass} style={buttonStyle} title={block.label}>{content}</Link>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  // Card animation: fade-in + slide-up (title first, then rest)
  useEffect(() => {
    const sectionEl = sectionRef.current;
    const contentEl = containerRef.current;
    if (!sectionEl || !contentEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const headingEls = contentEl.querySelectorAll(`.${styles.description__heading}`);
    const otherEls = contentEl.querySelectorAll(
      `.${styles['description__animate-item']}:not(.${styles.description__heading})`
    );
    const iconEl = contentEl.querySelector(`.${styles.description__icon}`);

    gsap.set(sectionEl, { opacity: 0, y: 50 });
    gsap.set([...headingEls], { opacity: 0, y: 20 });
    gsap.set([...otherEls], { opacity: 0, y: 20 });
    if (iconEl) gsap.set(iconEl, { opacity: 0, y: 20 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;
          hasAnimatedRef.current = true;

          const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

          tl.to(sectionEl, { opacity: 1, y: 0, duration: 0.6 });

          if (iconEl) {
            tl.to(iconEl, { opacity: 1, y: 0, duration: 0.4 }, '-=0.2');
          }

          if (headingEls.length) {
            tl.to(headingEls, { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }, '-=0.2');
          }

          if (otherEls.length) {
            tl.to(otherEls, { opacity: 1, y: 0, duration: 0.5, stagger: 0.08 }, '-=0.15');
          }

          observer.disconnect();
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(sectionEl);
    return () => observer.disconnect();
  }, []);

  if (!data) return null;

  return (
    <section
      ref={sectionRef}
      id={anchorId || undefined}
      className={sectionClassName}
      style={sectionStyle}
    >
      <div ref={containerRef} className={styles.description__container}>
        {iconMedia?.src && (
          <img
            className={`${styles.description__icon} ${styles['description__animate-item']}`}
            src={iconMedia.src}
            alt={iconMedia.alt}
            title={iconMedia.title}
            aria-hidden="true"
          />
        )}
        <div className={styles.description__content}>
          {contentBlocks.map((block, index) => renderContentBlock(block, index))}
        </div>
      </div>
    </section>
  );
};

export default Description;
